import React from 'react';
import { Facility } from '../../services/api/domains/facilities/types';
import { modalStyles as styles } from './styles';
import useEditableModal, { ModalMode } from './hooks/useEditableModal';
import ModalFieldRenderer from './ModalFieldRenderer';
import {
  toModalFields,
  getAlwaysDisabledFields,
} from '../../domains/facilities/adapters';
import LgmButton from '../LgmButton';
import { Edit, Save, X } from 'lucide-react';

interface FacilityModalProps {
  facility: Facility;
  initialMode?: 'EDIT' | 'VIEW';
  save: (editedFacility: Facility) => void;
  close: () => void;
}

const FacilityModal: React.FC<FacilityModalProps> = ({
  facility,
  initialMode = 'VIEW',
  save,
  close,
}) => {
  // Use the hook for all modal logic
  const {
    editedEntity: editedFacility,
    setEditedEntity: setEditedFacility,
    modalMode,
    hasUnsavedChanges,
    focusedField,
    setFocusedField,
    handleSave,
    handleClose,
    isFieldDisabled,
    getFloatingLabelStyle,
    getFloatingInputStyle,
    getPrimaryActionText,
    isSaving,
  } = useEditableModal({
    entity: facility,
    initialMode,
    onSave: save,
    onClose: close,
    alwaysDisabledFields: getAlwaysDisabledFields(),
    styles,
  });

  return (
    <div style={styles.modalOverlayStyle}>
      <div style={styles.modalContentStyle}>
        <div style={styles.modalHeaderStyle}>
          <h2 style={{ ...styles.modalTitleStyle, marginBottom: 0 }}>
            {modalMode === 'EDIT' ? 'Edit Facility' : 'Facility Details'}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <LgmButton
              variant="flat"
              color={modalMode === 'VIEW' ? 'warning' : 'save'}
              onClick={handleSave}
              disabled={
                isSaving || (modalMode === 'EDIT' && !hasUnsavedChanges)
              }
              style={styles.modalHeaderIconTextButtonStyle}
              aria-label={getPrimaryActionText()}
              title={getPrimaryActionText()}>
              {modalMode === 'VIEW' ? <Edit size={16} /> : <Save size={16} />}
              {getPrimaryActionText()}
            </LgmButton>
            <LgmButton
              variant="flat"
              onClick={handleClose}
              disabled={isSaving}
              style={styles.modalCloseXStyle}
              hoverStyle={styles.modalCloseXHoverStyle}
              pressedStyle={styles.modalCloseXPressedStyle}
              aria-label="Close">
              <X size={16} />
            </LgmButton>
          </div>
        </div>

        <div style={styles.modalBodyStyleNoFooter}>
          {/* All facility fields */}
          <ModalFieldRenderer
            fields={toModalFields()}
            entity={editedFacility}
            setEntity={setEditedFacility}
            mode={modalMode}
            focusedField={focusedField}
            setFocusedField={setFocusedField}
            isFieldDisabled={isFieldDisabled}
            getFloatingLabelStyle={getFloatingLabelStyle}
            getFloatingInputStyle={getFloatingInputStyle}
            styles={styles}
            missingMessage="Unknown"
          />
        </div>
      </div>
    </div>
  );
};

export default React.memo(FacilityModal);
export type { ModalMode };
